import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Input,
    Spinner,
    Label,
    Badge,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Progress,
    Table,
    Alert,
    Button,
    ButtonGroup
} from 'reactstrap';
import {
    BsFillBarChartLineFill,
    BsDownload,
    BsGrid3X3Gap,
    BsList,
    BsBarChart,
    BsPieChart,
    BsCalendar3,
    BsPeople,
    BsBoxSeam,
    BsScissors
} from 'react-icons/bs';
import { GiWheat } from 'react-icons/gi'
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { themeColors } from '../../../style/color';

// Enhanced chart options
const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top',
            labels: {
                usePointStyle: true,
                padding: 20,
                font: {
                    size: 12,
                    weight: '500'
                }
            }
        },
        tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: themeColors.primary,
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
        }
    },
    scales: {
        x: {
            grid: {
                display: false
            },
            ticks: {
                font: {
                    size: 11,
                    weight: '500'
                }
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
                font: {
                    size: 11
                }
            }
        }
    },
    elements: {
        bar: {
            borderRadius: 6,
            borderSkipped: false,
        }
    }
};

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top'
        }
    },
    scales: {
        x: {
            grid: {
                display: false
            }
        },
        y: {
            beginAtZero: true,
            grid: {
                color: 'rgba(0, 0, 0, 0.1)'
            }
        }
    },
    elements: {
        line: {
            tension: 0.4
        },
        point: {
            radius: 6,
            hoverRadius: 8
        }
    }
};

const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'right',
            labels: {
                usePointStyle: true,
                padding: 15
            }
        }
    },
    cutout: '60%'
};

const CropGraphicalReport = () => {
    const [loading, setLoading] = useState(true);
    const [organization, setOrganization] = useState('116');
    const [activeTab, setActiveTab] = useState('overview');
    const [viewMode, setViewMode] = useState('grid');
    const [selectedMetric, setSelectedMetric] = useState('all');

    const [distributionGraphData, setDistributionGraphData] = useState([]);
    const [trainingGraphData, setTrainingGraphData] = useState([]);
    const [sowingGraphData, setSowingGraphData] = useState([]);
    const [harvestingGraphData, setHarvestingGraphData] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [cropSummaryData, setCropSummaryData] = useState([]);
    const [rawData, setRawData] = useState({});

    useEffect(() => {
        get_graph();
    }, [organization]);

    const processDataForCharts = (distribution, training, sowing, harvesting) => {
        // Get unique organizations
        const organizations = [...new Set([
            ...distribution.map(d => d.org_name),
            ...training.map(t => t.org_name),
            ...sowing.map(s => s.org_name),
            ...harvesting.map(h => h.org_name)
        ])];

        // Process distribution data
        const distributionData = organizations.map(org_name => {
            const orgDistribution = distribution.filter(d => d.org_name === org_name);
            return orgDistribution.reduce((sum, item) => sum + parseInt(item.total_distribution || 0), 0);
        });

        // Process training data
        const trainingData = organizations.map(org_name => {
            const orgTraining = training.filter(t => t.org_name === org_name);
            return orgTraining.reduce((sum, item) => sum + parseInt(item.total_farmer || 0), 0);
        });

        // Process sowing data
        const sowingData = organizations.map(org_name => {
            const orgSowing = sowing.filter(s => s.org_name === org_name);
            return orgSowing.reduce((sum, item) => sum + parseInt(item.total_area || 0), 0);
        });

        // Process harvesting data (extract numbers from yield strings)
        const harvestingData = organizations.map(org_name => {
            const orgHarvesting = harvesting.filter(h => h.org_name === org_name);
            return orgHarvesting.reduce((sum, item) => {
                const yieldValue = item.crop_yield ? parseInt(item.crop_yield.split('(')[0].trim()) || 0 : 0;
                return sum + yieldValue;
            }, 0);
        });

        // Create chart datasets
        const distributionChart = {
            labels: organizations.map(org => org),
            datasets: [{
                label: "Distribution",
                data: distributionData,
                backgroundColor: `${themeColors.primary}cc`,
                borderColor: themeColors.primary,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const trainingChart = {
            labels: organizations.map(org => org),
            datasets: [{
                label: "Farmers Trained",
                data: trainingData,
                backgroundColor: `${themeColors.secondary}cc`,
                borderColor: themeColors.secondary,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const sowingChart = {
            labels: organizations.map(org => org),
            datasets: [{
                label: "Area Sown",
                data: sowingData,
                backgroundColor: `${themeColors.info}cc`,
                borderColor: themeColors.info,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        const harvestingChart = {
            labels: organizations.map(org => org),
            datasets: [{
                label: "Crop Yield",
                data: harvestingData,
                backgroundColor: `${themeColors.danger}cc`,
                borderColor: themeColors.danger,
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            }]
        };

        // Combined chart
        const combinedChart = {
            labels: organizations.map(org => org),
            datasets: [
                {
                    label: "Distribution",
                    data: distributionData,
                    backgroundColor: `${themeColors.primary}99`,
                    borderColor: themeColors.primary,
                    type: 'bar'
                },
                {
                    label: "Farmers Trained",
                    data: trainingData,
                    backgroundColor: `${themeColors.secondary}99`,
                    borderColor: themeColors.secondary,
                    type: 'bar'
                },
                {
                    label: "Area Sown",
                    data: sowingData,
                    backgroundColor: `${themeColors.info}99`,
                    borderColor: themeColors.info,
                    type: 'bar'
                },
                {
                    label: "Crop Yield",
                    data: harvestingData,
                    backgroundColor: `${themeColors.danger}99`,
                    borderColor: themeColors.danger,
                    type: 'line',
                    tension: 0.4
                }
            ]
        };

        // Summary chart - Distribution by organization
        const summaryChart = {
            labels: organizations.map(org => `Org ${org}`),
            datasets: [{
                data: distributionData,
                backgroundColor: [
                    themeColors.primary,
                    themeColors.secondary,
                    themeColors.info,
                    themeColors.danger,
                    themeColors.success,
                    themeColors.warning,
                    '#6366f1',
                    '#ec4899'
                ],
                borderWidth: 2,
                borderColor: '#fff'
            }]
        };

        return {
            distributionChart,
            trainingChart,
            sowingChart,
            harvestingChart,
            combinedChart,
            summaryChart,
            totals: {
                distribution: distributionData.reduce((a, b) => a + b, 0),
                training: trainingData.reduce((a, b) => a + b, 0),
                sowing: sowingData.reduce((a, b) => a + b, 0),
                harvesting: harvestingData.reduce((a, b) => a + b, 0)
            }
        };
    };

    const get_graph = async () => {
        setLoading(true);
        const postData = [{
            "org_id": sessionStorage.getItem('org_id'),
            "user_code": sessionStorage.getItem('user_id'),
            "password": sessionStorage.getItem('user_pass')
        }];

        try {
            // Execute all API calls concurrently
            const [distributionResponse, trainingResponse, sowingResponse, harvestingResponse] = await Promise.all([
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_distribution_crop_1',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_training',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_sowing_1',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                }),
                axios({
                    method: 'post',
                    url: mainurl + 'Dashboard/db_harvesting',
                    data: postData,
                    headers: { 'Content-Type': 'application/json' }
                })
            ]);

            // Extract data from responses
            const distribution = distributionResponse.data;
            const training = trainingResponse.data;
            const sowing = sowingResponse.data;
            const harvesting = harvestingResponse.data;

            // Process the data
            const processedData = processDataForCharts(distribution, training, sowing, harvesting);

            // Update state with processed data
            setDistributionGraphData(processedData.distributionChart);
            setTrainingGraphData(processedData.trainingChart);
            setSowingGraphData(processedData.sowingChart);
            setHarvestingGraphData(processedData.harvestingChart);
            setCombinedData(processedData.combinedChart);
            setCropSummaryData(processedData.summaryChart);

            setRawData({
                distribution,
                training,
                sowing,
                harvesting,
                totals: processedData.totals
            });

        } catch (error) {
            console.error('Error fetching graph data:', error);
            // Handle error state - you might want to show an error message to the user
            // setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const totalDistribution = rawData.totals?.distribution || 0;
    const totalTraining = rawData.totals?.training || 0;
    const totalSowing = rawData.totals?.sowing || 0;
    const totalHarvesting = rawData.totals?.harvesting || 0;

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const ChartCard = ({ title, icon: Icon, data, total, color, gradient, description }) => (
        <Card className="h-100 shadow-sm border-0">
            <CardHeader className="bg-gradient" style={{ background: gradient }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Icon size={20} className="me-2" style={{ color: color }} />
                        <div>
                            <CardTitle className="mb-0 fw-semibold">{title}</CardTitle>
                            <small className="text-muted">{description}</small>
                        </div>
                    </div>
                    <div className="text-end">
                        <h4 className="mb-0 fw-bold" style={{ color: color }}>{total.toLocaleString()}</h4>
                        <small className="text-muted">Total</small>
                    </div>
                </div>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '250px' }}>
                        <Spinner style={{ color: color }} />
                    </div>
                ) : (
                    <div style={{ height: '250px' }}>
                        <Bar options={barChartOptions} data={data} />
                    </div>
                )}
            </CardBody>
        </Card>
    );

    // Key Insight
    const generateKeyInsights = () => {
        if (!rawData.distribution || rawData.distribution.length === 0) {
            return {
                topPerformers: [],
                insights: [],
                recommendations: []
            };
        }

        // Calculate performance metrics for each organization
        const orgPerformance = rawData.distribution.map(dist => {
            const orgId = dist.org_id;
            const orgName = dist.org_name;

            // Get corresponding data from other datasets
            const trainingData = rawData.training?.find(t => t.org_code === orgId) || {};
            const sowingData = rawData.sowing?.find(s => s.org_id === orgId) || {};
            const harvestingData = rawData.harvesting?.find(h => h.org_id === orgId) || {};

            const distribution = parseInt(dist.total_distribution) || 0;
            const farmersTrained = parseInt(trainingData.total_farmer) || 0;
            const areaSown = parseInt(sowingData.total_area) || 0;
            const yieldValue = harvestingData.crop_yield ?
                parseInt(harvestingData.crop_yield.split('(')[0].trim()) || 0 : 0;

            // Calculate performance score (weighted average)
            const performanceScore = (
                (distribution * 0.3) +
                (farmersTrained * 0.3) +
                (areaSown * 0.2) +
                (yieldValue * 0.2)
            );

            return {
                orgId,
                orgName,
                distribution,
                farmersTrained,
                areaSown,
                yieldValue,
                performanceScore
            };
        }).sort((a, b) => b.performanceScore - a.performanceScore);

        // Generate insights
        const insights = [];
        const recommendations = [];

        // Top performer insight
        if (orgPerformance.length > 0) {
            const topOrg = orgPerformance[0];
            const totalDist = rawData.totals?.distribution || 0;
            const percentage = totalDist > 0 ? ((topOrg.distribution / totalDist) * 100).toFixed(1) : 0;

            insights.push({
                type: 'success',
                title: 'Top Performer',
                message: `${topOrg.orgName} leads with ${topOrg.distribution} seed distributions (${percentage}% of total).`
            });
        }

        // Training vs Distribution analysis
        const avgTrainingRatio = orgPerformance.reduce((sum, org) => {
            return sum + (org.distribution > 0 ? org.farmersTrained / org.distribution : 0);
        }, 0) / orgPerformance.length;

        const lowTrainingOrgs = orgPerformance.filter(org =>
            org.distribution > 0 && (org.farmersTrained / org.distribution) < avgTrainingRatio
        );

        if (lowTrainingOrgs.length > 0) {
            insights.push({
                type: 'warning',
                title: 'Training Gap',
                message: `${lowTrainingOrgs.length} organization(s) have lower farmer training ratios compared to seed distribution.`
            });

            recommendations.push({
                title: 'Increase Training Programs',
                message: `Focus on training programs for: ${lowTrainingOrgs.map(org => org.orgName).join(', ')}`
            });
        }

        // Sowing efficiency analysis
        const orgWithHighSowing = orgPerformance.filter(org => org.areaSown > org.distribution);
        if (orgWithHighSowing.length > 0) {
            insights.push({
                type: 'info',
                title: 'Efficient Utilization',
                message: `${orgWithHighSowing.length} organization(s) achieved area sown exceeding seed distribution.`
            });
        }

        // Harvest analysis
        const harvestingOrgs = orgPerformance.filter(org => org.yieldValue > 0);
        if (harvestingOrgs.length === 0) {
            insights.push({
                type: 'warning',
                title: 'Harvest Pending',
                message: 'No harvest data available yet. Monitor upcoming harvest cycles.'
            });

            recommendations.push({
                title: 'Monitor Harvest',
                message: 'Set up regular harvest monitoring and data collection systems.'
            });
        } else {
            const avgYield = harvestingOrgs.reduce((sum, org) => sum + org.yieldValue, 0) / harvestingOrgs.length;
            insights.push({
                type: 'success',
                title: 'Harvest Progress',
                message: `${harvestingOrgs.length} organization(s) have harvest data with average yield of ${avgYield.toFixed(1)}.`
            });
        }

        // Resource utilization insight
        const utilizationRate = rawData.totals ?
            ((rawData.totals.sowing / rawData.totals.distribution) * 100).toFixed(1) : 0;

        if (utilizationRate > 0) {
            const utilizationType = utilizationRate > 80 ? 'success' : utilizationRate > 60 ? 'warning' : 'danger';
            insights.push({
                type: utilizationType,
                title: 'Resource Utilization',
                message: `Overall seed utilization rate is ${utilizationRate}% (area sown vs seeds distributed).`
            });

            if (utilizationRate < 70) {
                recommendations.push({
                    title: 'Improve Utilization',
                    message: 'Focus on better seed utilization and sowing efficiency programs.'
                });
            }
        }

        return {
            topPerformers: orgPerformance.slice(0, 5), // Top 5 performers
            insights,
            recommendations
        };
    };
    //

    return (
        <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1 fw-bold d-flex align-items-center">
                                <GiWheat className="me-2" style={{ color: themeColors.success }} />
                                Organization Wise Crop Analysis
                            </h5>
                            <p className="text-muted mb-0">Performance overview across organizations for cultivation</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <Badge color="success" pill className="px-3 py-2">
                                <BsCalendar3 size={14} className="me-1" />
                                Live Data
                            </Badge>
                            {/* <div className="d-flex align-items-center">
                                <Label className="me-2 small fw-semibold">Organization:</Label>
                                <Input
                                    type="select"
                                    value={organization}
                                    onChange={(e) => setOrganization(e.target.value)}
                                    className="form-control-sm"
                                    style={{ minWidth: '120px' }}
                                >
                                    <option value="116">Organization 116</option>
                                    <option value="117">Organization 117</option>
                                </Input>
                            </div> */}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Key Metrics Summary */}
            <Row className="mb-4">
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <CardBody className="text-center">
                            <BsBoxSeam size={24} className="mb-2" />
                            <h4 className="mb-1">{totalDistribution}</h4>
                            <small>Total Seeds Distributed</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-secondary text-white">
                        <CardBody className="text-center">
                            <BsPeople size={24} className="mb-2" />
                            <h4 className="mb-1">{totalTraining}</h4>
                            <small>Farmers Trained</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-info text-white">
                        <CardBody className="text-center">
                            <GiWheat size={24} className="mb-2" />
                            <h4 className="mb-1">{totalSowing}</h4>
                            <small>Total Area Sown</small>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} className="mb-3">
                    <Card className="border-0 shadow-sm bg-danger text-white">
                        <CardBody className="text-center">
                            <BsScissors size={24} className="mb-2" />
                            <h4 className="mb-1">{totalHarvesting}</h4>
                            <small>Total Yield</small>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Navigation and Controls */}
            <Row className="mb-4">
                <Col>
                    <Card className="border-0 shadow-sm">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <Nav tabs className="border-0">
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'overview' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('overview')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <BsGrid3X3Gap className="me-2" />
                                            Overview
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'comparison' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('comparison')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <BsBarChart className="me-2" />
                                            Comparison
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={`${activeTab === 'summary' ? 'active' : ''} fw-semibold px-4`}
                                            onClick={() => toggleTab('summary')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <BsPieChart className="me-2" />
                                            Summary
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <div className="d-flex align-items-center gap-2">
                                    <ButtonGroup size="sm">
                                        <Button
                                            color={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                                            onClick={() => setViewMode('grid')}
                                        >
                                            <BsGrid3X3Gap />
                                        </Button>
                                        <Button
                                            color={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                            onClick={() => setViewMode('list')}
                                        >
                                            <BsList />
                                        </Button>
                                    </ButtonGroup>
                                    <Button color="outline-primary" size="sm">
                                        <BsDownload className="me-1" />
                                        Export
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                    {/* Individual Charts */}
                    <Row className={viewMode === 'grid' ? 'mb-4' : ''}>
                        {viewMode === 'grid' ? (
                            <>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Seed Distribution"
                                        icon={BsBoxSeam}
                                        data={distributionGraphData}
                                        total={totalDistribution + ' kg'}
                                        color={themeColors.primary}
                                        gradient="linear-gradient(135deg, #3b82f620 0%, #1e40af10 100%)"
                                        description="Distributed by organization"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Training Programs"
                                        icon={BsPeople}
                                        data={trainingGraphData}
                                        total={totalTraining}
                                        color={themeColors.secondary}
                                        gradient="linear-gradient(135deg, #8b5cf620 0%, #7c3aed10 100%)"
                                        description="Farmers trained"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Sowing Activity"
                                        icon={GiWheat}
                                        data={sowingGraphData}
                                        total={totalSowing}
                                        color={themeColors.info}
                                        gradient="linear-gradient(135deg, #06b6d420 0%, #0891b210 100%)"
                                        description="Total area sown"
                                    />
                                </Col>
                                <Col lg={6} className="mb-4">
                                    <ChartCard
                                        title="Harvest Yield"
                                        icon={BsScissors}
                                        data={harvestingGraphData}
                                        total={totalHarvesting}
                                        color={themeColors.danger}
                                        gradient="linear-gradient(135deg, #ef444420 0%, #dc262610 100%)"
                                        description="yield harvested"
                                    />
                                </Col>
                            </>
                        ) : (
                            <Col>
                                <Card className="shadow-sm border-0">
                                    <CardBody>
                                        <Table responsive hover>
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Organization</th>
                                                    <th>Seeds Distributed</th>
                                                    <th>Farmers Trained</th>
                                                    <th>Area Sown</th>
                                                    <th>Crop Yield</th>
                                                    <th>Performance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rawData.distribution?.map((item, index) => (
                                                    <tr key={item.org_id}>
                                                        <td className="fw-semibold">Organization {item.org_id}</td>
                                                        <td>{item.total_distribution}</td>
                                                        <td>
                                                            {rawData.training?.find(t => t.org_code === item.org_id)?.total_farmer || '0'}
                                                        </td>
                                                        <td>
                                                            {rawData.sowing?.find(s => s.org_id === item.org_id)?.total_area || '0'}
                                                        </td>
                                                        <td>
                                                            {rawData.harvesting?.find(h => h.org_id === item.org_id)?.crop_yield || '0'}
                                                        </td>
                                                        <td>
                                                            <Progress
                                                                value={parseInt(item.total_distribution) > 50 ? 80 : 40}
                                                                color={parseInt(item.total_distribution) > 50 ? "success" : "warning"}
                                                                style={{ height: '6px' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                )) || []}
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </TabPane>

                <TabPane tabId="comparison">
                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-gradient" style={{ background: 'linear-gradient(135deg, #f59e0b20 0%, #d97706 10 100%)' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <BsFillBarChartLineFill size={20} className="me-2" style={{ color: themeColors.warning }} />
                                            <div>
                                                <CardTitle className="mb-0 fw-semibold">Comparative Analysis - All Organizations</CardTitle>
                                                <small className="text-muted">All metrics across organizations for cultivation</small>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                            <Spinner color="warning" />
                                        </div>
                                    ) : (
                                        <div style={{ height: '400px' }}>
                                            <Bar options={barChartOptions} data={combinedData} />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane tabId="summary">
                    <Row>
                        <Col lg={8}>
                            <Card className="shadow-sm border-0 h-100">
                                <CardHeader>
                                    <CardTitle className="mb-0 fw-semibold">Distribution Summary by Organization</CardTitle>
                                </CardHeader>
                                <CardBody>
                                    {loading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                            <Spinner color="primary" />
                                        </div>
                                    ) : (
                                        <div style={{ height: '300px' }}>
                                            <Doughnut options={doughnutOptions} data={cropSummaryData} />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <CardBody>
                                {(() => {
                                    const keyInsights = generateKeyInsights();

                                    return (
                                        <>
                                            <div className="mb-4">
                                                <h6 className="fw-semibold mb-3">Top Performing Organizations</h6>
                                                {keyInsights.topPerformers.length > 0 ? (
                                                    keyInsights.topPerformers.map((org, index) => (
                                                        <div key={org.orgId} className="d-flex justify-content-between align-items-center mb-3">
                                                            <div className="d-flex align-items-center">
                                                                <Badge
                                                                    color={index === 0 ? "success" : index === 1 ? "info" : "secondary"}
                                                                    className="me-2"
                                                                >
                                                                    {index + 1}
                                                                </Badge>
                                                                <span>{org.orgName}</span>
                                                            </div>
                                                            <div className="text-end">
                                                                <span className="fw-semibold">{org.distribution} seeds</span>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {org.farmersTrained} farmers, {org.areaSown} area
                                                                </small>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <Alert color="info" className="small">
                                                        No performance data available yet.
                                                    </Alert>
                                                )}
                                            </div>

                                            <div className="mb-4">
                                                <h6 className="fw-semibold mb-3">Key Insights</h6>
                                                {keyInsights.insights.length > 0 ? (
                                                    keyInsights.insights.map((insight, index) => (
                                                        <Alert key={index} color={insight.type} className="small mb-2">
                                                            <strong>{insight.title}:</strong> {insight.message}
                                                        </Alert>
                                                    ))
                                                ) : (
                                                    <Alert color="info" className="small">
                                                        Analyzing data... Insights will appear as more data becomes available.
                                                    </Alert>
                                                )}
                                            </div>

                                            {keyInsights.recommendations.length > 0 && (
                                                <div>
                                                    <h6 className="fw-semibold mb-3">Recommendations</h6>
                                                    {keyInsights.recommendations.map((rec, index) => (
                                                        <Alert key={index} color="primary" className="small mb-2">
                                                            <strong>{rec.title}:</strong> {rec.message}
                                                        </Alert>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </CardBody>
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>
        </div>
    );
};

export default CropGraphicalReport;